import React, { useState } from 'react';
import { Eye, Edit, Search, ChevronDown, ChevronUp, Users, Trash2, X, User, Phone, Calendar, Mail, MapPin, CreditCard, Clock } from 'lucide-react';

const ViewPatient = ({ onBack, onViewPatient, onEditPatient }) => {
  // Sample patient data - in a real app, this would come from an API
  const [patients, setPatients] = useState([
    {
      id: 1,
      patientCode: 'PT001',
      firstName: 'Rajesh',
      lastName: 'Kumar',
      cellNo: '9876543210',
      whatsappNo: '9876543210',
      email: 'rajesh.kumar@example.com',
      dateOfBirth: '1985-05-15',
      address: '123 Main Street, Mumbai, Maharashtra',
      lastVisit: '2023-10-15T14:30:00'
    },
    {
      id: 2,
      patientCode: 'PT002',
      firstName: 'Priya',
      lastName: 'Sharma',
      cellNo: '8765432109',
      whatsappNo: '8765432109',
      email: 'priya.sharma@example.com',
      dateOfBirth: '1990-08-22',
      address: '456 Oak Avenue, Delhi',
      lastVisit: '2023-10-18T10:15:00'
    },
    {
      id: 3,
      patientCode: 'PT003',
      firstName: 'Amit',
      lastName: 'Verma',
      cellNo: '7654321098',
      whatsappNo: '7654321098',
      email: 'amit.verma@example.com',
      dateOfBirth: '1982-12-10',
      address: '789 Pine Road, Bangalore, Karnataka',
      lastVisit: '2023-10-20T16:45:00'
    },
    {
      id: 4,
      patientCode: 'PT004',
      firstName: 'Sneha',
      lastName: 'Patel',
      cellNo: '6543210987',
      whatsappNo: '6543210987',
      email: 'sneha.patel@example.com',
      dateOfBirth: '1995-03-30',
      address: '321 Elm Street, Chennai, Tamil Nadu',
      lastVisit: '2023-10-22T09:20:00'
    },
    {
      id: 5,
      patientCode: 'PT005',
      firstName: 'Vikram',
      lastName: 'Singh',
      cellNo: '9432109876',
      whatsappNo: '9432109876',
      email: 'vikram.singh@example.com',
      dateOfBirth: '1978-07-18',
      address: '654 Cedar Lane, Kolkata, West Bengal',
      lastVisit: '2023-10-25T11:30:00'
    }
  ]);

  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('patientCode');
  const [sortDirection, setSortDirection] = useState('asc');
  const [alertModal, setAlertModal] = useState({ 
    isOpen: false, 
    type: '', 
    patient: null, 
    message: '' 
  });

  // Filter patients based on search term
  const filteredPatients = patients.filter(patient =>
    patient.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    patient.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    patient.patientCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
    patient.cellNo.includes(searchTerm) ||
    patient.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Sort patients
  const sortedPatients = [...filteredPatients].sort((a, b) => {
    if (a[sortField] < b[sortField]) {
      return sortDirection === 'asc' ? -1 : 1;
    }
    if (a[sortField] > b[sortField]) {
      return sortDirection === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const formatDateTime = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const handleView = (patient) => {
    setAlertModal({
      isOpen: true,
      type: 'view',
      patient,
      message: `${patient.firstName} ${patient.lastName}'s Details`
    });
  };

  const handleEdit = (patient) => {
    setAlertModal({
      isOpen: true,
      type: 'edit',
      patient,
      message: `You are about to edit ${patient.firstName} ${patient.lastName}'s details.`
    });
  };

  const handleDelete = (patient) => {
    setAlertModal({
      isOpen: true,
      type: 'delete',
      patient,
      message: `Are you sure you want to delete ${patient.firstName} ${patient.lastName}? This action cannot be undone.`
    });
  };

  const confirmAction = () => {
    const { type, patient } = alertModal;
    
    if (type === 'edit') {
      onEditPatient(patient);
    } else if (type === 'delete') {
      // Remove the patient from the state
      setPatients(prevPatients => 
        prevPatients.filter(p => p.id !== patient.id)
      );
    }
    
    // Close the modal
    setAlertModal({ isOpen: false, type: '', patient: null, message: '' });
    
    // Show success message for delete
    if (type === 'delete') {
      setTimeout(() => {
        setAlertModal({
          isOpen: true,
          type: 'success',
          patient: null,
          message: `Patient ${patient.firstName} ${patient.lastName} has been successfully deleted.`
        });
      }, 300);
    }
  };

  const closeModal = () => {
    setAlertModal({ isOpen: false, type: '', patient: null, message: '' });
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronDown className="w-4 h-4 opacity-30" />;
    return sortDirection === 'asc' ? 
      <ChevronUp className="w-4 h-4" /> : 
      <ChevronDown className="w-4 h-4" />;
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-md">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={onBack}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                </svg>
              </button>
              <div>
                <h1 className="text-2xl font-bold">View Patients</h1>
                <p className="text-blue-100">Manage patient records</p>
              </div>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <Users className="w-6 h-6" />
            </div>
          </div>
        </div>

        {/* Search and Controls */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search patients by name, code, phone, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="text-sm text-gray-600">
              {filteredPatients.length} patient(s) found
            </div>
          </div>
        </div>

        {/* Patients Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('patientCode')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Patient Code</span>
                    <SortIcon field="patientCode" />
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('firstName')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Patient Name</span>
                    <SortIcon field="firstName" />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cell Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  WhatsApp Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedPatients.length > 0 ? (
                sortedPatients.map((patient) => (
                  <tr key={patient.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-blue-600">{patient.patientCode}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{patient.firstName} {patient.lastName}</div>
                      <div className="text-sm text-gray-500">
                        DOB: {formatDate(patient.dateOfBirth)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {patient.cellNo}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {patient.whatsappNo}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {patient.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleView(patient)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50 transition-colors"
                          title="View Patient"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(patient)}
                          className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50 transition-colors"
                          title="Edit Patient"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(patient)}
                          className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50 transition-colors"
                          title="Delete Patient"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center">
                    <div className="flex flex-col items-center justify-center text-gray-500">
                      <Users className="w-12 h-12 mb-2 opacity-50" />
                      <p className="text-lg font-medium">No patients found</p>
                      <p className="text-sm">Try adjusting your search criteria</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination (optional for future implementation) */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing <span className="font-medium">1</span> to <span className="font-medium">{sortedPatients.length}</span> of{' '}
              <span className="font-medium">{sortedPatients.length}</span> results
            </div>
            {/* Pagination controls would go here */}
          </div>
        </div>
      </div>

      {/* Alert Modal */}
      {alertModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className={`bg-white rounded-lg shadow-xl max-w-2xl w-full ${alertModal.type === 'view' ? 'max-w-4xl' : 'max-w-md'}`}>
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">
                {alertModal.type === 'edit' && 'Edit Patient'}
                {alertModal.type === 'delete' && 'Delete Patient'}
                {alertModal.type === 'view' && 'Patient Details'}
                {alertModal.type === 'success' && 'Success'}
              </h3>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              {alertModal.type !== 'view' && <p className="mb-4">{alertModal.message}</p>}
              
              {alertModal.type === 'view' && alertModal.patient && (
                <div>
                  <div className="flex items-center mb-6">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                      <User className="w-8 h-8 text-blue-600" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">
                        {alertModal.patient.firstName} {alertModal.patient.lastName}
                      </h2>
                      <p className="text-gray-500">Patient Code: {alertModal.patient.patientCode}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h3 className="text-lg font-semibold text-blue-800 mb-3 flex items-center">
                        <CreditCard className="w-5 h-5 mr-2" />
                        Personal Information
                      </h3>
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm text-blue-600">Patient Code</p>
                          <p className="font-medium">{alertModal.patient.patientCode}</p>
                        </div>
                        <div>
                          <p className="text-sm text-blue-600">Date of Birth</p>
                          <p className="font-medium">{formatDate(alertModal.patient.dateOfBirth)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-blue-600">Last Visit</p>
                          <p className="font-medium">{formatDateTime(alertModal.patient.lastVisit)}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h3 className="text-lg font-semibold text-green-800 mb-3 flex items-center">
                        <Phone className="w-5 h-5 mr-2" />
                        Contact Information
                      </h3>
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm text-green-600">Cell Number</p>
                          <p className="font-medium">{alertModal.patient.cellNo}</p>
                        </div>
                        <div>
                          <p className="text-sm text-green-600">WhatsApp Number</p>
                          <p className="font-medium">{alertModal.patient.whatsappNo}</p>
                        </div>
                        <div>
                          <p className="text-sm text-green-600">Email Address</p>
                          <p className="font-medium flex items-center">
                            <Mail className="w-4 h-4 mr-1" />
                            {alertModal.patient.email}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-green-600">Address</p>
                          <p className="font-medium flex items-center">
                            <MapPin className="w-4 h-4 mr-1" />
                            {alertModal.patient.address}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex justify-end gap-3 p-4 border-t">
              {alertModal.type === 'success' ? (
                <button
                  onClick={closeModal}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  OK
                </button>
              ) : alertModal.type === 'view' ? (
                <button
                  onClick={closeModal}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Close
                </button>
              ) : (
                <>
                  <button
                    onClick={closeModal}
                    className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmAction}
                    className={`px-4 py-2 text-white rounded-md ${
                      alertModal.type === 'delete' 
                        ? 'bg-red-600 hover:bg-red-700' 
                        : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                  >
                    {alertModal.type === 'delete' ? 'Delete' : 'Confirm'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ViewPatient;